import React, { useCallback, useEffect, useRef, useState } from 'react';

// Página PRIVADA de revisión (solo la dueña/dueño del negocio). Entra con un enlace
// enviado por correo (sin contraseña). La base de datos solo deja leer y aprobar a
// los correos autorizados (reglas RLS en Supabase), así que aunque alguien conozca
// esta dirección no ve nada. No aparece en el menú ni en buscadores.
const SB = 'https://jcbxwtuhshcyzlhpikxr.supabase.co';
const ANON = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpjYnh3dHVoc2hjeXpsaHBpa3hyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3OTA0NDc5MzAsImV4cCI6MjEwNjAyMzkzMH0.TCCTiwKNauedwdX3R0_Ag0MCZId2nZGmuLIN86FcH-U';
const STORE_KEY = 'noor_rev_session';
type Sess = {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  email: string;
};
type Row = {
  id: string;
  created_at: string;
  form_name: string | null;
  client_name: string | null;
  client_email: string | null;
  status: string;
  draft_path: string | null;
  draft_report: any;
  draft_at: string | null;
  delivered_email_sent_at: string | null;
  email_error: string | null;
  answers?: any;
};
let memSess: Sess | null = null;
function loadSess(): Sess | null {
  try {
    const s = window.localStorage.getItem(STORE_KEY);
    if (s) return JSON.parse(s);
  } catch {/* sin almacenamiento */}
  return memSess;
}
function saveSess(s: Sess | null) {
  memSess = s;
  try {
    if (s) window.localStorage.setItem(STORE_KEY, JSON.stringify(s));else window.localStorage.removeItem(STORE_KEY);
  } catch {/* sin almacenamiento */}
}
function decodeEmail(jwt: string): string {
  try {
    const p = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(p)).email || '';
  } catch {
    return '';
  }
}
function readHash(): {
  sess?: Sess;
  error?: string;
} {
  const h = window.location.hash.replace(/^#/, '');
  if (!h) return {};
  const p = new URLSearchParams(h);
  if (p.get('error')) return {
    error: p.get('error_description') || p.get('error') || 'error'
  };
  const at = p.get('access_token');
  const rt = p.get('refresh_token');
  if (!at || !rt) return {};
  const exp = Number(p.get('expires_at')) || Math.floor(Date.now() / 1000) + 3600;
  return {
    sess: {
      access_token: at,
      refresh_token: rt,
      expires_at: exp,
      email: decodeEmail(at)
    }
  };
}
const STATUS: Record<string, {
  t: string;
  c: string;
}> = {
  received: {
    t: 'Pendiente de revisión',
    c: 'bg-amber-100 text-amber-900'
  },
  ready: {
    t: 'Enviando…',
    c: 'bg-blue-100 text-blue-900'
  },
  delivered: {
    t: 'Entregado',
    c: 'bg-green-100 text-green-900'
  },
  needs_email: {
    t: 'Falta correo o archivo',
    c: 'bg-red-100 text-red-900'
  }
};
function formLabel(n: string | null): string {
  const [ds, l] = String(n || '').split('-');
  return (ds || '').toUpperCase().replace(/^DS(\d)/, 'DS-$1') + (l ? ' (' + l.toUpperCase() + ')' : '');
}
function fmtDate(s: string | null): string {
  if (!s) return '';
  try {
    return new Date(s).toLocaleString('es-US', {
      timeZone: 'America/New_York',
      dateStyle: 'short',
      timeStyle: 'short'
    });
  } catch {
    return s;
  }
}
function flatten(a: any, out: any[] = []): any[] {
  if (Array.isArray(a)) {
    a.forEach(x => flatten(x, out));
    return out;
  }
  if (a && typeof a === 'object') {
    if (typeof a.id === 'string' && 'value' in a && (a.name !== undefined || a.type !== undefined)) out.push(a);
    for (const k of Object.keys(a)) if (a[k] && typeof a[k] === 'object') flatten(a[k], out);
  }
  return out;
}
function showVal(v: any): string {
  if (v === null || v === undefined || v === '') return '';
  if (Array.isArray(v)) return v.map(showVal).filter(Boolean).join(', ');
  if (typeof v === 'object') return Object.values(v).filter(x => x !== null && x !== '' && typeof x !== 'object').join(', ');
  return String(v);
}
// El país "Estados Unidos" no tiene casilla en los formularios del Departamento de Estado:
// se oculta en la lista de respuestas para no confundir a quien revisa.
function isUsCountry(q: any): boolean {
  if (!/country$|pa[ií]s$|qu[oô]c gia$|nasyon$|국가$/i.test(String(q?.name || ''))) return false;
  const v = String(q?.value ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return v === '' || /^(us|usa|unitedstates|unitedstatesofamerica|estadosunidos|etatsunis|hoaky|my|etazini)$/.test(v);
}
function setAnswer(answers: any, id: string, val: string): any {
  const c = JSON.parse(JSON.stringify(answers));
  const walk = (a: any) => {
    if (Array.isArray(a)) a.forEach(walk);else if (a && typeof a === 'object') {
      if (a.id === id && 'value' in a) a.value = val;
      for (const k of Object.keys(a)) if (a[k] && typeof a[k] === 'object') walk(a[k]);
    }
  };
  walk(c);
  return c;
}
const ascii = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
export function RevisionPage() {
  const [sess, setSess] = useState<Sess | null>(null);
  const [authMsg, setAuthMsg] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [tab, setTab] = useState<'pend' | 'done'>('pend');
  const [sel, setSel] = useState<Row | null>(null);
  const [err, setErr] = useState('');
  const sessRef = useRef<Sess | null>(null);
  useEffect(() => {
    document.title = 'Revisión';
    let m = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const created = !m;
    if (!m) {
      m = document.createElement('meta');
      m.name = 'robots';
      document.head.appendChild(m);
    }
    m.content = 'noindex,nofollow';
    return () => {
      if (created && m) m.remove();
    };
  }, []);
  useEffect(() => {
    const h = readHash();
    if (h.error) setAuthMsg('El enlace no sirvió o ya venció. Pida uno nuevo.');
    if (h.sess) {
      saveSess(h.sess);
      try {
        window.history.replaceState(null, '', window.location.pathname);
      } catch {/* nada */}
      setSess(h.sess);
      sessRef.current = h.sess;
      return;
    }
    const s = loadSess();
    if (s) {
      setSess(s);
      sessRef.current = s;
    }
  }, []);
  const logout = useCallback(() => {
    saveSess(null);
    sessRef.current = null;
    setSess(null);
    setRows(null);
    setSel(null);
  }, []);
  const token = useCallback(async (): Promise<string> => {
    let s = sessRef.current;
    if (!s) throw new Error('nosess');
    if (s.expires_at - 60 < Math.floor(Date.now() / 1000)) {
      const r = await fetch(SB + '/auth/v1/token?grant_type=refresh_token', {
        method: 'POST',
        headers: {
          apikey: ANON,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_token: s.refresh_token
        })
      });
      if (!r.ok) {
        logout();
        throw new Error('expired');
      }
      const d = await r.json();
      s = {
        access_token: d.access_token,
        refresh_token: d.refresh_token,
        expires_at: d.expires_at || Math.floor(Date.now() / 1000) + 3600,
        email: s.email
      };
      saveSess(s);
      sessRef.current = s;
      setSess(s);
    }
    return s.access_token;
  }, [logout]);
  const call = useCallback(async (path: string, init: RequestInit = {}) => {
    const t = await token();
    const headers: Record<string, string> = {
      apikey: ANON,
      Authorization: 'Bearer ' + t,
      ...(init.headers as any || {})
    };
    return fetch(SB + path, {
      ...init,
      headers
    });
  }, [token]);
  const loadRows = useCallback(async () => {
    setErr('');
    try {
      const r = await call('/rest/v1/form_submissions?select=id,created_at,form_name,client_name,client_email,status,draft_path,draft_report,draft_at,delivered_email_sent_at,email_error&order=created_at.desc&limit=200');
      if (r.status === 401) {
        logout();
        return;
      }
      if (!r.ok) {
        setErr('No se pudo cargar la lista (' + r.status + ').');
        return;
      }
      setRows(await r.json());
    } catch {
      setErr('No se pudo cargar la lista.');
    }
  }, [call, logout]);
  useEffect(() => {
    if (sess) loadRows();
  }, [sess, loadRows]);
  async function sendLink(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    setAuthMsg('');
    try {
      const r = await fetch(SB + '/auth/v1/otp?redirect_to=' + encodeURIComponent(window.location.origin + '/revision'), {
        method: 'POST',
        headers: {
          apikey: ANON,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email.trim(),
          create_user: false
        })
      });
      if (r.ok) setAuthMsg('Listo. Si ese correo está autorizado, le llegó un enlace de acceso. Ábralo en este mismo navegador.');else setAuthMsg('No se pudo enviar el enlace. Espere un minuto e intente de nuevo.');
    } catch {
      setAuthMsg('No se pudo enviar el enlace.');
    }
    setSending(false);
  }
  const wrap = 'max-w-7xl mx-auto px-4 sm:px-6 py-8';
  if (!sess) {
    return <div className="max-w-md mx-auto px-4 py-16" data-id="element-722" data-mp="pxkhi3f4jtra">
        <h1 className="text-2xl font-bold text-brand-textPrimary mb-2" data-id="element-723" data-mp="l3q41hq44b0n">Revisión</h1>
        <p className="text-brand-textSecondary mb-6" data-id="element-724" data-mp="3tk2v2s7rl8lp">Acceso privado. Escriba su correo y le enviamos un enlace para entrar.</p>
        <form onSubmit={sendLink} className="space-y-3" data-id="element-725" data-mp="3okq72455pvhj">
          <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="correo@ejemplo.com" className="w-full rounded-xl border border-brand-border px-4 py-3" data-id="element-726" data-mp="243pbg6sw1v88" />
          <button disabled={sending} className="w-full px-4 py-3 rounded-xl bg-brand-textPrimary text-white font-semibold disabled:opacity-50" data-id="element-727" data-mp="1v3105juvq8z1">{sending ? 'Enviando…' : 'Enviarme el enlace'}</button>
        </form>
        {authMsg && <p className="mt-4 text-sm text-brand-textPrimary rounded-xl border border-brand-border bg-white p-3" data-id="element-728" data-mp="2nr6gluewhbt2">{authMsg}</p>}
      </div>;
  }
  if (sel) {
    return <Detail row={sel} call={call} onBack={() => {
      setSel(null);
      loadRows();
    }} data-id="element-729" data-mp="2dt8vhfsc7v4o" />;
  }
  const pend = (rows || []).filter(r => r.status !== 'delivered');
  const done = (rows || []).filter(r => r.status === 'delivered');
  const list = tab === 'pend' ? pend : done;
  return <div className={wrap} data-id="element-730" data-mp="12g6guq9stfn5">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2" data-id="element-731" data-mp="17cwvd95yg36g">
        <h1 className="text-2xl font-bold text-brand-textPrimary" data-id="element-732" data-mp="17atvlxf02voj">Revisión de formularios</h1>
        <div className="flex items-center gap-3 text-sm text-brand-textSecondary" data-id="element-733" data-mp="3mw9d0yc04e7s">
          <span data-id="element-734" data-mp="3rsgbk622infp">{sess.email}</span>
          <button onClick={loadRows} className="underline" data-id="element-735" data-mp="3d4d3mr4gt1o9">Actualizar</button>
          <button onClick={logout} className="underline" data-id="element-736" data-mp="3d291oeq4mcqm">Salir</button>
        </div>
      </div>
      <div className="flex gap-2 mb-4" data-id="element-737" data-mp="3i1db96gyqchh">
        <button onClick={() => setTab('pend')} className={'px-4 py-2 rounded-full border ' + (tab === 'pend' ? 'bg-brand-textPrimary text-white' : 'bg-white')} data-id="element-738" data-mp="3435h16bb9pa0">Pendientes ({pend.length})</button>
        <button onClick={() => setTab('done')} className={'px-4 py-2 rounded-full border ' + (tab === 'done' ? 'bg-brand-textPrimary text-white' : 'bg-white')} data-id="element-739" data-mp="2u60tp35qbnrm">Entregados ({done.length})</button>
      </div>
      {err && <p className="text-red-700 mb-4" data-id="element-740" data-mp="sdutc9a96csq">{err}</p>}
      {rows === null && !err && <p className="text-brand-textSecondary" data-id="element-741" data-mp="xcz0kudma0gf">Cargando…</p>}
      {rows !== null && list.length === 0 && <p className="text-brand-textSecondary" data-id="element-742" data-mp="2hv03vs7s6zq5">No hay envíos en esta lista.</p>}
      <div className="space-y-2" data-id="element-743" data-mp="2hsx3qfcwzq7p">
        {list.map(r => {
        const rep = r.draft_report || {};
        const warns = (rep.overflow?.length || 0) + (rep.missingRequired?.length || 0) + (rep.unmapped?.length || 0);
        const st = STATUS[r.status] || {
          t: r.status,
          c: 'bg-gray-100 text-gray-900'
        };
        return <button key={r.id} onClick={() => setSel(r)} className="w-full text-left rounded-xl border border-brand-border bg-white p-4 hover:shadow flex items-center justify-between gap-3 flex-wrap" data-id="element-744" data-mp="30l7ytn1bujv9">
              <div data-id="element-745" data-mp="3ak718nvi1j9t">
                <div className="font-semibold text-brand-textPrimary" data-id="element-746" data-mp="3aim72zd3k2ch">{formLabel(r.form_name)} — {r.client_name || r.client_email || 'sin nombre'}</div>
                <div className="text-sm text-brand-textSecondary" data-id="element-747" data-mp="1g5wdyjujyx65">{fmtDate(r.created_at)}{r.draft_path ? '' : ' · sin borrador automático'}</div>
              </div>
              <div className="flex items-center gap-2" data-id="element-748" data-mp="1b8nr45ygm4yk">
                {warns > 0 && <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-900" data-id="element-749" data-mp="20w0a0vbcsc7x">{warns} aviso(s)</span>}
                <span className={'text-xs px-2 py-1 rounded-full ' + st.c} data-id="element-750" data-mp="24x2a1ed3gsay">{st.t}</span>
              </div>
            </button>;
      })}
      </div>
    </div>;
}
function Detail({
  row,
  call,
  onBack
}: {
  row: Row;
  call: (p: string, i?: RequestInit) => Promise<Response>;
  onBack: () => void;
}) {
  const [full, setFull] = useState<Row | null>(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfVer, setPdfVer] = useState(0);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState('');
  const [editVal, setEditVal] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [confirm, setConfirm] = useState(false);
  const loadPdf = useCallback(async (path: string | null) => {
    if (!path) {
      setPdfUrl('');
      return;
    }
    try {
      // t= y no-store: evita que el navegador o la red muestren una copia vieja del PDF después de editar
      const r = await call('/storage/v1/object/authenticated/drafts/' + path.split('/').map(encodeURIComponent).join('/') + '?t=' + Date.now(), {
        cache: 'no-store'
      });
      if (!r.ok) {
        setMsg('No se pudo abrir el PDF (' + r.status + ').');
        return;
      }
      const b = await r.blob();
      setPdfUrl(old => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(new Blob([b], {
          type: 'application/pdf'
        }));
      });
      setPdfVer(v => v + 1);
    } catch {
      setMsg('No se pudo abrir el PDF.');
    }
  }, [call]);
  const loadRow = useCallback(async () => {
    const r = await call('/rest/v1/form_submissions?select=*&id=eq.' + row.id);
    if (!r.ok) {
      setMsg('No se pudo cargar el envío.');
      return null;
    }
    const d = await r.json();
    const f: Row | undefined = d[0];
    if (f) {
      setFull(f);
      const qs = flatten(f.answers);
      const guess = f.client_email || (qs.find(q => q.type === 'EmailInput' && q.value) || qs.find(q => /@/.test(String(q.value))))?.value || '';
      setToEmail(old => old || String(guess || ''));
    }
    return f || null;
  }, [call, row.id]);
  useEffect(() => {
    loadRow().then(f => {
      if (f) loadPdf(f.draft_path);
    });
    return () => {
      setPdfUrl(old => {
        if (old) URL.revokeObjectURL(old);
        return '';
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const qs = full ? flatten(full.answers) : [];
  const shown = qs.filter(q => !isUsCountry(q));
  const rep = full?.draft_report || {};
  async function regen(newAnswers: any, qid: string, newVal: string) {
    setBusy(true);
    setMsg('Guardando y regenerando el PDF…');
    try {
      const p = await call('/rest/v1/form_submissions?id=eq.' + row.id, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Prefer: 'return=representation'
        },
        body: JSON.stringify({
          answers: newAnswers
        })
      });
      if (!p.ok) {
        setMsg('No se pudo guardar el cambio (' + p.status + ').');
        setBusy(false);
        return;
      }
      const saved = await p.json().catch(() => []);
      const savedQ = Array.isArray(saved) && saved[0] ? flatten(saved[0].answers).find(q => q.id === qid) : null;
      if (!savedQ || String(savedQ.value) !== newVal) {
        setMsg('El cambio NO se guardó (la base de datos no lo aceptó). No se envió nada al cliente. Avise a soporte.');
        setBusy(false);
        return;
      }
      const g = await fetch(SB + '/functions/v1/fill-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: ANON,
          Authorization: 'Bearer ' + ANON
        },
        body: JSON.stringify({
          record: {
            id: row.id
          }
        })
      });
      const gd = await g.json().catch(() => ({}));
      if (!gd.ok) {
        setMsg('El cambio se guardó, pero no se pudo regenerar el PDF. No apruebe hasta que se regenere.');
        setBusy(false);
        return;
      }
      const f = await loadRow();
      if (f) await loadPdf(f.draft_path);
      setMsg('Listo: cambio guardado y PDF regenerado. El PDF de la izquierda ya muestra el cambio.');
    } catch {
      setMsg('Ocurrió un problema al regenerar. Revise el PDF antes de aprobar.');
    }
    setBusy(false);
  }
  async function approve() {
    if (!full || !full.draft_path) return;
    const to = toEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) {
      setMsg('Escriba un correo válido para el cliente.');
      return;
    }
    setBusy(true);
    setMsg('Enviando…');
    try {
      const ds = formLabel(full.form_name).split(' ')[0];
      const lastQ = qs.find(q => /^(apellido|last name|họ|성|non fanmi|family name)/i.test(String(q.name || '')));
      const who = ascii(String(lastQ?.value || full.client_name || 'cliente')) || 'cliente';
      const dest = row.id + '/' + Date.now() + '/Noor_' + ds + '_' + who + '.pdf';
      const c = await call('/storage/v1/object/copy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bucketId: 'drafts',
          sourceKey: full.draft_path,
          destinationBucket: 'deliveries',
          destinationKey: dest
        })
      });
      if (!c.ok) {
        setMsg('No se pudo preparar el PDF para enviar (' + c.status + ').');
        setBusy(false);
        return;
      }
      const u = await call('/rest/v1/form_submissions?id=eq.' + row.id, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Prefer: 'return=minimal'
        },
        body: JSON.stringify({
          client_email: to,
          delivery_path: dest,
          status: 'ready',
          email_error: null
        })
      });
      if (!u.ok) {
        setMsg('No se pudo marcar como listo (' + u.status + ').');
        setBusy(false);
        return;
      }
      for (let i = 0; i < 12; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const f = await loadRow();
        if (f && f.status === 'delivered') {
          setMsg('Enviado al cliente (' + to + ').');
          setConfirm(false);
          setBusy(false);
          return;
        }
        if (f && f.email_error) {
          setMsg('El correo no salió: ' + f.email_error);
          setBusy(false);
          return;
        }
      }
      setMsg('Se marcó como listo; el correo debería salir en un momento. Actualice la lista para confirmar.');
    } catch {
      setMsg('Ocurrió un problema al enviar.');
    }
    setBusy(false);
  }
  const delivered = full?.status === 'delivered';
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6" data-id="element-751" data-mp="3flbukxaoq1lk">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4" data-id="element-752" data-mp="31n578b3d3k05">
        <button onClick={onBack} className="underline text-brand-textSecondary" data-id="element-753" data-mp="36m8b3s7lvk6j">← Volver a la lista</button>
        <h1 className="text-xl font-bold text-brand-textPrimary" data-id="element-754" data-mp="2rx2du9sbw41a">{formLabel(row.form_name)} — {full?.client_name || row.client_name || row.client_email || ''}</h1>
      </div>
      {msg && <p className="mb-4 rounded-xl border border-brand-border bg-white p-3 text-brand-textPrimary" data-id="element-755" data-mp="2ixytl2hfdil7">{msg}</p>}
      <div className="grid lg:grid-cols-2 gap-6" data-id="element-756" data-mp="2my8x88hqvwww">
        <div data-id="element-757" data-mp="2mwo5eqmtes2q">
          {pdfUrl ? <iframe key={pdfVer} title="PDF" src={pdfUrl} className="w-full rounded-xl border border-brand-border bg-white" style={{
          height: '82vh',
          minHeight: 600
        }} data-id="element-758" data-mp="28yzk5hou5ix5" /> : <div className="rounded-xl border border-brand-border bg-white p-6 text-brand-textSecondary" data-id="element-759" data-mp="1ucak9eqo93q8">
              {full && !full.draft_path ? 'Este formulario todavía no se llena automáticamente. Use las respuestas de la derecha.' : 'Cargando PDF…'}
            </div>}
        </div>
        <div className="space-y-4" data-id="element-760" data-mp="1zbwxvpaewlzk">
          {rep.overflow?.length || rep.missingRequired?.length || rep.unmapped?.length ? <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-brand-textPrimary" data-id="element-761" data-mp="2dad8hrbeawmz">
              <p className="font-semibold mb-1" data-id="element-762" data-mp="28bsfocyh3f8k">Avisos del llenado automático</p>
              {rep.missingRequired?.length > 0 && <p data-id="element-763" data-mp="2n3ask1w1ctnp">Faltan datos: {rep.missingRequired.join(', ')}</p>}
              {rep.overflow?.length > 0 && <p data-id="element-764" data-mp="2i2wrx4wrklzp">No cupieron o requieren revisión: {rep.overflow.map((o: any) => o.field + (o.value ? ' («' + o.value + '»)' : '') + (o.note ? ' – ' + o.note : '')).join('; ')}</p>}
              {rep.unmapped?.length > 0 && <p data-id="element-765" data-mp="2ry82db15371n">Respuestas que no tienen casilla en el PDF: {rep.unmapped.map((o: any) => o.canon).join(', ')}</p>}
            </div> : null}
          <div className="rounded-xl border border-brand-border bg-white" data-id="element-766" data-mp="2ziwioswnea77">
            <p className="px-4 py-3 font-semibold border-b border-brand-border" data-id="element-767" data-mp="3djh4zz1ruo5g">Respuestas del cliente</p>
            {!delivered && full?.draft_path && <p className="px-4 py-2 text-sm bg-amber-50 border-b border-brand-border text-brand-textPrimary" data-id="element-768" data-mp="38l5s1fg6jxj0">Para corregir un dato, pulse el botón <strong data-id="element-769" data-mp="2ksp8f0gsnxhl">Editar</strong> junto a la respuesta, escriba el cambio y pulse <strong data-id="element-770" data-mp="2gv1cpv7a20on">Guardar y regenerar</strong>.</p>}
            <ul className="divide-y divide-brand-border" data-id="element-771" data-mp="2ulejvvcchbcx">
              {shown.map(q => {
              const v = showVal(q.value);
              const editable = typeof q.value === 'string' || q.value === null || q.value === undefined;
              return <li key={q.id} className="px-4 py-2 text-sm" data-id="element-772" data-mp="2227l5beqdu3m">
                    <div className="text-brand-textSecondary" data-id="element-773" data-mp="1x4xy22etduiy">{q.name}</div>
                    {editId === q.id ? <div className="flex flex-col gap-2 mt-1" data-id="element-774" data-mp="clbztdufbaau">
                        <input value={editVal} onChange={e => setEditVal(e.target.value)} className="w-full rounded-lg border border-brand-border px-3 py-2" data-id="element-775" data-mp="hf6q504janiw" />
                        <div className="flex gap-2 flex-wrap" data-id="element-776" data-mp="8luvwounal7s">
                          <button disabled={busy} onClick={() => {
                      const a = setAnswer(full?.answers, q.id, editVal);
                      setEditId('');
                      regen(a, q.id, editVal);
                    }} className="px-4 py-2 rounded-lg bg-brand-textPrimary text-white font-semibold disabled:opacity-50" data-id="element-777" data-mp="8m35a6rk78e8">Guardar y regenerar</button>
                          <button onClick={() => setEditId('')} className="px-4 py-2 rounded-lg border border-brand-border" data-id="element-778" data-mp="mcq5629234bw">Cancelar</button>
                        </div>
                      </div> : <div className="flex items-start justify-between gap-2" data-id="element-779" data-mp="375jwdzxdos6c">
                        <span className="font-medium text-brand-textPrimary break-words" data-id="element-780" data-mp="3c44vtx4t37tq">{v || <em className="text-brand-textSecondary" data-id="element-781" data-mp="2s0pa1sxy496m">(vacío)</em>}</span>
                        {editable && !delivered && full?.draft_path && <button onClick={() => {
                    setEditId(q.id);
                    setEditVal(v);
                  }} className="text-sm px-3 py-1 rounded-lg border border-brand-border bg-white text-brand-textPrimary font-semibold shrink-0 hover:shadow" data-id="element-782" data-mp="2nznhfuyjavdm">Editar</button>}
                      </div>}
                  </li>;
            })}
            </ul>
          </div>
          {!delivered && full?.draft_path && <div className="rounded-xl border border-brand-border bg-white p-4 space-y-3" data-id="element-783" data-mp="24cz6k8uaobu1">
              <p className="font-semibold" data-id="element-784" data-mp="20bdpebaq9be1">Aprobar y enviar al cliente</p>
              <input type="email" value={toEmail} onChange={e => setToEmail(e.target.value)} placeholder="correo del cliente" className="w-full rounded-xl border border-brand-border px-4 py-2" data-id="element-785" data-mp="1vbhtkzc6oq9k" />
              {!confirm ? <button disabled={busy} onClick={() => setConfirm(true)} className="w-full px-4 py-3 rounded-xl bg-brand-textPrimary text-white font-semibold disabled:opacity-50" data-id="element-786" data-mp="3pm4pa9r8x015">Aprobar y enviar</button> : <div className="flex gap-2" data-id="element-787" data-mp="ase3a4nti8p7">
                  <button disabled={busy} onClick={approve} className="flex-1 px-4 py-3 rounded-xl bg-green-700 text-white font-semibold disabled:opacity-50" data-id="element-788" data-mp="atgru5xo3z1y">Confirmar: enviar PDF a {toEmail || 'el cliente'}</button>
                  <button disabled={busy} onClick={() => setConfirm(false)} className="px-4 py-3 rounded-xl border border-brand-border" data-id="element-789" data-mp="3eap6yi10gksz">Cancelar</button>
                </div>}
              <p className="text-xs text-brand-textSecondary" data-id="element-790" data-mp="3o5h79tlz9lrg">El cliente recibe el PDF como archivo adjunto. Después de enviar, el envío pasa a «Entregados».</p>
            </div>}
          {delivered && <p className="rounded-xl border border-green-300 bg-green-50 p-4 text-brand-textPrimary" data-id="element-791" data-mp="13ga0dahau2mi">Entregado al cliente el {fmtDate(full?.delivered_email_sent_at || null)}.</p>}
        </div>
      </div>
    </div>;
}